<div #timelineParent class="flex h-full w-full flex-col overflow-hidden select-none">
  @if (computedRows().length > 0) {
    <!-- Controls -->
    <div class="relative flex justify-between py-1 text-xs">
      <div class="flex items-center gap-3 pl-2">
        <!-- Toggle checkboxes -->
        <app-checkbox
          [id]="'show-color-coded-blocks'"
          [label]="'visualization.timeline.show_color_coded_blocks' | translate"
          [checked]="showColorCodedBlocks()"
          [onChange]="toggleColorCodedBlocks" />
        <app-checkbox
          [id]="'show-task-type-colors'"
          [label]="'visualization.timeline.show_task_type_colors' | translate"
          [checked]="showTaskTypeColors()"
          [onChange]="toggleTaskTypeColors" />
        <app-checkbox
          [id]="'show-temperature-background'"
          [label]="'visualization.timeline.show_temperature_background' | translate"
          [checked]="showTemperatureBackground()"
          [onChange]="toggleTemperatureBackground" />
        <app-checkbox
          [id]="'focus-on-hover'"
          [label]="'visualization.timeline.focus_on_hover' | translate"
          [checked]="focusOnHover()"
          [onChange]="toggleFocusOnHover" />
      </div>
      <div class="flex shrink-0 items-center gap-3 pr-2">
        @if (edit.editingHere()) {
          <div
            data-testid="timeline-edit-legend"
            class="text-text-secondary hidden items-center gap-3.5 text-[11.5px] xl:flex">
            <span class="inline-flex items-center gap-1.5">
              <i [class]="ICONS.handGrabbing" aria-hidden="true"></i>
              {{ 'features.plan.edit.gestures.drag_to_pin' | translate }}
            </span>
            <span class="inline-flex items-center gap-1.5">
              <kbd class="key-chip">{{ altKey | translate }}</kbd>
              {{ 'features.plan.edit.gestures.to_reorder' | translate }}
            </span>
            <span class="inline-flex items-center gap-1.5">
              <i [class]="ICONS.mouseRightClick" aria-hidden="true"></i>
              {{ 'features.plan.edit.gestures.right_click' | translate }}
            </span>
            <span class="bg-default h-4.5 w-px" aria-hidden="true"></span>
          </div>
          <button
            type="button"
            data-testid="timeline-new-route"
            class="btn btn-secondary"
            (click)="openNewRoute($event)">
            <i [class]="ICONS.plus + ' text-sm leading-none'" aria-hidden="true"></i>
            <span>{{ 'features.plan.edit.new_route.button' | translate }}</span>
          </button>
        }
        <div class="flex items-center gap-1.5">
          <span
            data-testid="timeline-time-step-label"
            class="text-text-secondary whitespace-nowrap"
            >{{ 'visualization.timeline.timestep' | translate }}</span
          >
          <app-select
            data-testid="timeline-time-step"
            class="mr-1 w-24"
            [items]="timeStepItems()"
            [value]="currentTimeStep()"
            (valueChange)="onTimeStepChange($any($event))" />
        </div>
      </div>
      <!-- Covers the controls rather than pushing the rows down under the dragging pointer. -->
      @if (editDrag() || editPlacing()) {
        <app-timeline-edit-strip
          class="absolute inset-0 z-20"
          [drag]="editDrag()"
          [placing]="editPlacing()" />
      }
    </div>

    <div class="min-h-0 w-full flex-1">
      <div class="relative">
        <div
          class="absolute top-0 left-0 z-10 h-14 w-[150px] border-r border-b border-zinc-300 bg-white dark:border-zinc-600 dark:bg-zinc-900"></div>
      </div>
      <cdk-virtual-scroll-viewport
        #contentViewport
        [itemSize]="cellHeight"
        [minBufferPx]="bufferMultiplier() * 64"
        [maxBufferPx]="bufferMultiplier() * 128"
        (click)="onTimelineBackgroundClick($event)"
        class="content-viewport h-full overflow-x-auto">
        <div
          #headersContainer
          data-testid="timeline-header"
          [style.top]="headerTop()"
          class="headers-container bg-elevated sticky z-10"
          style="scrollbar-width: none; -ms-overflow-style: none">
          <!-- Days header -->
          <div class="ml-[150px]">
            <div class="border-default flex h-8 border-t">
              @for (date of dates(); track trackTimelineDateContext($index, date)) {
                @if (date.left) {
                  <div
                    class="absolute -z-10 ml-[149px] w-0 border-x border-zinc-400 dark:border-zinc-600"
                    [style.left]="date.left"
                    [style.height]="contentHeightPlusHeader()"></div>
                }
                <div
                  class="text-text-tertiary shrink-0 px-1 pt-1 pb-2 text-sm"
                  [style.width]="date.width">
                  <p class="h-5 overflow-hidden">{{ date.date }}</p>
                </div>
              }
            </div>

            <!-- Hours header -->
            <div class="border-default flex h-6 border-b">
              @for (column of columns(); track trackTimelineColumn($index, column)) {
                <div class="border-weak shrink-0 border-r" [style.width]="columnWidth()">
                  <span class="text-text-tertiary block px-1 pb-1 text-xs">{{
                    column
                  }}</span>
                </div>
              }
            </div>
          </div>

          @if (nowTimeStepPosition() !== null) {
            <div
              data-testid="timeline-now-line"
              class="absolute top-0 left-0 -z-10 w-0"
              [style.left]="nowTimeStepPosition()">
              <div
                class="absolute top-0 left-1 w-1 border-x border-zinc-50 bg-red-600 dark:border-zinc-800"
                [style.height]="contentHeightPlusHeader()"></div>
              <div class="absolute -top-2 left-0 h-3 w-3 rounded-full bg-red-600"></div>
            </div>
          }

          @if (editDrag(); as drag) {
            @if (geometry(); as geometry) {
              <div class="pointer-events-none absolute inset-y-0 right-0 left-[150px]">
                <app-timeline-edit-overlay
                  layer="ruler"
                  [drag]="drag"
                  [geometry]="geometry"
                  [height]="contentHeightPlusHeader()" />
              </div>
            }
          }
        </div>

        <!-- Virtual scroll content for timeline rows -->
        <div
          *cdkVirtualFor="let row of computedRows(); trackBy: trackTimelineRow"
          class="timeline-row relative h-16"
          [style.width]="contentWidth()">
          <!-- Background grid cells for this row -->
          <div class="absolute ml-[150px] flex h-full w-full">
            @for (column of columns(); track trackTimelineColumn($index, column)) {
              <div
                class="border-weak shrink-0 border-r"
                [style.width]="columnWidth()"></div>
            }
          </div>

          <!-- Row content: temperature lines and tasks -->
          <div class="absolute flex h-full w-full flex-row">
            <div
              [attr.data-label]="row.name"
              [attr.data-row-uid]="row.uid"
              [attr.data-vehicle-uid]="row.route?.vehicle_uid"
              [attr.data-company-uid]="row.route?.company_uid"
              data-context="timeline-row-label"
              #timelineRowLabel
              [style.width]="`${cellLegendWidth}px`"
              class="border-default bg-surface hover:bg-interactive sticky left-0 z-10 flex h-16 items-center overflow-hidden border-r border-b px-2 transition-colors"
              [class]="edit.editingHere() ? 'py-1.5' : 'py-3'"
              (mouseenter)="onRowHover(row.uid)"
              (mouseleave)="onRowHover(null)"
              (click)="onMouseClickOnRow(row)"
              (keydown.enter)="onMouseClickOnRow(row)"
              (keydown.space)="onMouseClickOnRow(row)">
              <!-- The focus class goes here rather than on the sticky column itself: `opacity`
                   applies to an element's own background too, and this column's is the only
                   thing standing between the reader and the grid it is scrolling over. -->
              <div
                class="flex w-full min-w-0 items-center"
                [class]="focusClassForRow(row.uid)"
                [class.edit-route-frozen]="isRouteFrozen(row)">
                <!-- Color indicator -->
                <div
                  class="mr-1 h-6 w-2 rounded-full shadow-sm"
                  [style.background]="row.color"></div>
                <div class="flex flex-1 flex-col overflow-hidden">
                  <span
                    class="text-text-primary text-xs leading-tight font-medium break-all"
                    [class]="edit.editingHere() ? 'line-clamp-1' : 'line-clamp-2'">
                    {{ row.name }}
                  </span>
                  <span class="text-text-tertiary truncate text-[10px] leading-tight">
                    {{ row.uid }}
                  </span>
                  @if (routeChips().get(row.uid); as chips) {
                    <span
                      class="mt-0.5 flex min-w-0 gap-0.5"
                      data-testid="timeline-route-chips">
                      @for (chip of chips; track chip.kind) {
                        <span
                          class="border-weak bg-page text-text-secondary inline-flex min-w-0 items-center gap-0.5 rounded-sm border px-1 text-[10px] leading-4"
                          [class]="chip.kind === 'vehicle' ? 'flex-[2]' : 'flex-1'"
                          [attr.title]="chip.title">
                          <i [class]="chip.icon" class="shrink-0"></i>
                          <span class="truncate">{{ chip.name }}</span>
                          @if (chip.registration) {
                            <span
                              class="text-text-tertiary min-w-0 shrink-[3] truncate font-mono"
                              data-testid="timeline-route-registration">
                              {{ chip.registration }}
                            </span>
                          }
                        </span>
                      }
                    </span>
                  }
                </div>
                @if (row.lockCfg && row.lockCfg !== 'Unlocked') {
                  <i
                    [class]="
                      (row.lockCfg === 'RemovalLockedOnly'
                        ? 'text-amber-500 '
                        : 'text-red-500 ') + ICONS.lock
                    "
                    class="ml-1 shrink-0 text-xs">
                  </i>
                }
                @if (row.validated) {
                  <i
                    [class]="ICONS.checkCircle"
                    class="ml-1 shrink-0 text-xs text-green-600 dark:text-green-500"></i>
                }
              </div>
              @if (isRouteFrozen(row)) {
                <i
                  [class]="ICONS.lock"
                  class="text-text-secondary absolute top-1 right-1 shrink-0 text-xs"
                  data-testid="timeline-route-frozen"
                  role="img"
                  [attr.aria-label]="
                    'features.plan.edit.route_label.frozen' | translate
                  "></i>
              } @else if (isRouteTouched(row)) {
                <span
                  class="bg-accent-main absolute top-1.5 right-1.5 size-2 rounded-full"
                  data-testid="timeline-route-touched"
                  role="img"
                  [attr.title]="'features.plan.edit.route_label.touched' | translate"
                  [attr.aria-label]="
                    'features.plan.edit.route_label.touched' | translate
                  "></span>
              }
            </div>
            <app-tooltip
              [target]="timelineRowLabel"
              [title]="row.name"
              [content]="getRowTooltipContent(row)"
              [position]="'right'"
              [showHint]="false" />

            <div
              class="absolute ml-[150px] h-full w-full"
              [attr.data-edit-row]="row.uid"
              [attr.data-pending]="pendingRouteUids().has(row.uid) || null"
              [class.edit-route-dim]="
                reorderRouteUid() !== null && reorderRouteUid() !== row.uid
              "
              [class.edit-route-frozen]="isRouteFrozen(row)"
              [class.edit-drop-row]="dropRowUid() === row.uid"
              (click)="onMouseClickOnRow(row)"
              (keydown)="onMouseClickOnRow(row)">
              @if (row.tasks.length === 0) {
                <span
                  data-testid="timeline-route-empty"
                  class="text-text-tertiary pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-xs">
                  {{ 'features.plan.edit.route_label.no_stops' | translate }}
                </span>
              }
              <!-- Temperature backgrounds -->
              @for (task of row.tasks; track trackTask($index, task)) {
                <!-- Background tint for temperature zone - covers task + transition -->
                @if (showTemperatureBackground()) {
                  @if (task.temperatureSegments && task.temperatureSegments.length > 1) {
                    <div
                      class="pointer-events-none absolute top-0 flex h-full flex-col"
                      style="opacity: 0.15"
                      [style.left]="`${task._leftPos}px`"
                      [style.width]="`${task._width ?? 0}px`">
                      @for (segment of task.temperatureSegments; track $index) {
                        <div class="flex-1" [style.background-color]="segment.color"></div>
                      }
                    </div>
                  } @else if (task.lazyProps?.lineColor) {
                    <div
                      class="temperature-bg"
                      [style.left]="`${task._leftPos}px`"
                      [style.width]="`${task._width ?? 0}px`"
                      [style.background]="task.lazyProps?.lineColor"></div>
                  }
                }
              }

              <!-- Task rendering (markers, blocks, etc.) -->
              @for (task of row.tasks; track trackTask($index, task)) {
                @if (task.kind === 'drive') {
                  <!-- Temperature line (solid or dashed based on task.dashedLine) -->
                  <div
                    #timelineTemperatureLine
                    class="absolute top-4 h-1/2"
                    [class]="focusClassForTask(row.uid, task)"
                    [class.pending-hatch-line]="pendingRouteUids().has(row.uid)"
                    (mouseenter)="onTaskHover(task.actionUid)"
                    (mouseleave)="onTaskHover(null)"
                    [style.left]="`${task._leftPos ?? 0}px`"
                    [style.width]="`${task._width ?? 0}px`">
                    @if (
                      showTemperatureBackground() &&
                      task.temperatureSegments &&
                      task.temperatureSegments.length > 0
                    ) {
                      <!-- One line per temperature/emptiness group, centered as a stack -->
                      <div
                        class="relative top-4 flex flex-col gap-0"
                        [style.transform]="
                          'translateY(' + -(task.temperatureSegments.length - 1) + 'px)'
                        ">
                        @for (segment of task.temperatureSegments; track $index) {
                          <div
                            class="h-0 border-t-2"
                            [class.border-solid]="!segment.dashed"
                            [class.border-dashed]="segment.dashed"
                            [style.border-color]="segment.color ?? '#656464'"></div>
                        }
                      </div>
                    } @else {
                      <div
                        class="relative top-4 h-0 border-t-2"
                        [class.border-solid]="!task.dashedLine"
                        [class.border-dashed]="task.dashedLine"
                        [style.border-color]="
                          showTemperatureBackground()
                            ? (task.lazyProps?.lineColor ?? '#656464')
                            : '#656464'
                        "></div>
                    }
                  </div>
                  <app-tooltip
                    class="fixed z-100"
                    [target]="timelineTemperatureLine"
                    [title]="getTooltipTitle(task)"
                    [content]="getTooltipContent(task)"
                    [position]="'top'"
                    [hintText]="getTooltipHint(task)" />
                }

                @if (
                  task.kind === 'work' || task.kind === 'start' || task.kind === 'finish'
                ) {
                  @if (task.kind === 'start') {
                    <div
                      #timelineStartMarker
                      class="group absolute top-5 z-5"
                      [class]="focusClassForTask(row.uid, task)"
                      data-context="timeline-start-marker"
                      [attr.data-route-uid]="task.routeUid"
                      [attr.data-start]="task.startDate.toJSON()"
                      [class.edit-start-draggable]="edit.canDragStart(row, task)"
                      [class.edit-start-source]="startDragRouteUid() === row.uid"
                      style="color: #656464"
                      [style.left]="`${(task?._leftPos ?? 0) - 12}px`"
                      (pointerdown)="edit.onStartPointerDown(row, task, $event)"
                      (click)="onTaskClick(task, $event)"
                      (keydown)="onTaskClick(task, $event)"
                      (mouseenter)="onTaskHover(task.actionUid)"
                      (mouseleave)="onTaskHover(null)">
                      <svg
                        class="timeline-marker -translate-x-2"
                        viewBox="0 0 50 50"
                        width="28"
                        height="28"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z"
                          fill="white"
                          filter="url(#shadow)" />
                        <path
                          d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z"
                          fill="currentColor" />
                        <path
                          d="M34.5 24.134C35.1667 24.5189 35.1667 25.4811 34.5 25.866L21 33.6603C20.3333 34.0452 19.5 33.564 19.5 32.7942V17.2058C19.5 16.436 20.3333 15.9548 21 16.3397L34.5 24.134Z"
                          fill="white" />
                      </svg>
                      @if (shownRouteStarts()[row.uid]) {
                        <span
                          data-testid="timeline-start-pin"
                          role="img"
                          class="edit-start-pin pointer-events-none"
                          [attr.aria-label]="'features.plan.edit.start_set' | translate">
                          <i [class]="ICONS.pushPinFill"></i>
                        </span>
                      }
                    </div>
                    <app-tooltip
                      class="fixed z-100"
                      [target]="timelineStartMarker"
                      [title]="getTooltipTitle(task)"
                      [content]="getTooltipContent(task)"
                      [position]="'top'"
                      [showHint]="true"
                      [hintText]="
                        edit.canDragStart(row, task)
                          ? startGesturesHint()
                          : getTooltipHint(task)
                      "
                      [interactive]="true" />
                  } @else if (task.kind === 'finish') {
                    <div
                      #timelineFinishMarker
                      class="group absolute top-5"
                      [class]="focusClassForTask(row.uid, task)"
                      style="color: #656464"
                      [style.left]="`${(task?._leftPos ?? 0) + (task?._width ?? 0) - 12}px`"
                      (click)="onTaskClick(task, $event)"
                      (keydown)="onTaskClick(task, $event)"
                      (mouseenter)="onTaskHover(task.actionUid)"
                      (mouseleave)="onTaskHover(null)">
                      <svg
                        class="timeline-marker translate-x-2"
                        viewBox="0 0 50 50"
                        width="28"
                        height="28"
                        xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M25 47C37.1503 47 47 37.1503 47 25C47 12.8497 37.1503 3 25 3C12.8497 3 3 12.8497 3 25C3 37.1503 12.8497 47 25 47Z"
                          fill="white" />
                        <path
                          d="M25 42C34.3888 42 42 34.3888 42 25C42 15.6112 34.3888 8 25 8C15.6112 8 8 15.6112 8 25C8 34.3888 15.6112 42 25 42Z"
                          fill="currentColor" />
                        <path
                          d="M25 19H31V14.6084C32.8206 15.6619 34.3371 17.1794 35.3906 19H31V25H37C37 27.1864 36.4123 29.2346 35.3906 31H31V35.3906C29.2346 36.4123 27.1864 37 25 37V31H19V35.3906C17.1796 34.3372 15.6628 32.8204 14.6094 31H19V25H13C13 22.8136 13.5877 20.7654 14.6094 19H19V14.6084C20.7653 13.5869 22.8137 13 25 13V19ZM25 31H31V25H25V31ZM19 25H25V19H19V25Z"
                          fill="white" />
                      </svg>
                    </div>
                    <app-tooltip
                      class="fixed z-100"
                      [target]="timelineFinishMarker"
                      [title]="getTooltipTitle(task)"
                      [content]="getTooltipContent(task)"
                      [position]="'top'"
                      [showHint]="true"
                      [hintText]="getTooltipHint(task)"
                      [interactive]="true" />
                  } @else {
                    <div
                      #timelineTask
                      class="timeline-task group relative"
                      [class]="focusClassForTask(row.uid, task)"
                      [attr.data-context]="
                        task.stepIndex !== undefined ? 'timeline-task' : null
                      "
                      [attr.data-route-uid]="task.routeUid"
                      [attr.data-action-uid]="task.actionUid"
                      [attr.data-step-index]="task.stepIndex"
                      [attr.data-site-uid]="task.siteUid"
                      [attr.data-job-uids]="task.jobUids?.join(' ')"
                      [attr.data-start]="task.startDate.toJSON()"
                      [style.left]="`${task._leftPos}px`"
                      [style.width]="`${task._width}px`"
                      (pointerdown)="edit.onBlockPointerDown(row, task, $event)"
                      (mouseenter)="onTaskHover(task.actionUid)"
                      (mouseleave)="onTaskHover(null)">
                      @if (task.actionUid && commentCountByActionUid()[task.actionUid]) {
                        <div class="pointer-events-none absolute -top-1.5 right-0 z-10">
                          <app-comment-indicator
                            [count]="commentCountByActionUid()[task.actionUid!]" />
                        </div>
                      }
                      <!-- Floating label for small blocks -->
                      @if (isBlockTooSmall(task._width)) {
                        <div
                          class="floating-task-label pointer-events-none absolute top-0 left-0 z-5">
                          <span
                            class="inline-block truncate rounded bg-zinc-800/90 px-1 py-0.5 text-[8px] font-semibold text-white shadow-sm dark:bg-zinc-900/90">
                            {{ truncateTaskName(task) }}
                          </span>
                        </div>
                      }
                      <!-- Merged work block design: Original colors + optional color segments -->
                      <div
                        class="timeline-task-block absolute top-2 w-full cursor-pointer overflow-hidden rounded-md border border-white/20"
                        [class.h-12]="
                          !(
                            showColorCodedBlocks() &&
                            task.colorSegments &&
                            task.colorSegments.length > 0
                          )
                        "
                        [class.flex]="
                          showColorCodedBlocks() &&
                          task.colorSegments &&
                          task.colorSegments.length > 0
                        "
                        [class.flex-col]="
                          showColorCodedBlocks() &&
                          task.colorSegments &&
                          task.colorSegments.length > 0
                        "
                        [class.pending-hatch]="pendingRouteUids().has(row.uid)"
                        [class.edit-draggable]="edit.canDrag(row, task)"
                        [class.edit-drag-source]="
                          !!task.actionUid && dragSourceUids().has(task.actionUid)
                        "
                        [class.stripes_bg]="
                          showTaskTypeColors() && task.lazyProps?.isDeliveryPickup
                        "
                        [class.normal_bg]="
                          !(showTaskTypeColors() && task.lazyProps?.isDeliveryPickup)
                        "
                        [style.max-width]="`${task._width}px`"
                        [style.--color1]="
                          showTaskTypeColors() && task.lazyProps?.isDeliveryPickup
                            ? taskColors.delivery
                            : null
                        "
                        [style.--color2]="
                          showTaskTypeColors() && task.lazyProps?.isDeliveryPickup
                            ? taskColors.pickup
                            : null
                        "
                        [style.--color]="
                          !showTaskTypeColors()
                            ? taskDefaultColor
                            : !task.lazyProps?.isDeliveryPickup
                              ? task.lazyProps?.color
                              : null
                        "
                        [style.box-shadow]="
                          isTaskRinged(task)
                            ? '0 0 0 1px var(--accent-main), 0 0 0 2px var(--accent-interactive)'
                            : null
                        "
                        (click)="onTaskClick(task, $event)"
                        (keydown)="onTaskClick(task, $event)">
                        @if (
                          task.kind === 'work' &&
                          task.actionUid &&
                          shownPins()[task.actionUid]
                        ) {
                          <i
                            data-testid="timeline-pin"
                            role="img"
                            class="pointer-events-none absolute top-1 right-1 text-[10px] text-white drop-shadow-sm"
                            [class]="ICONS.pushPinFill"
                            [attr.aria-label]="'features.plan.edit.pinned' | translate"></i>
                        }
                        <!-- Top section: Original design with colored background and text -->
                        <div class="task-content px-1 py-1">
                          <p
                            class="overflow-x-hidden p-0 text-[10px] font-semibold whitespace-nowrap text-white drop-shadow-sm">
                            {{ task.name }}
                          </p>
                          <p
                            class="overflow-x-hidden p-0 text-[9px] whitespace-nowrap text-white drop-shadow-sm">
                            {{ task.startDate | date: 'HH:mm' }} -
                            {{ task.endDate | date: 'HH:mm' }}
                          </p>
                        </div>
                        <!-- Bottom section: Color segments (conditional) -->
                        @if (
                          showColorCodedBlocks() &&
                          task.colorSegments &&
                          task.colorSegments.length > 0
                        ) {
                          <div class="color-segment-container mt-0.5 flex">
                            @for (segment of task.colorSegments; track $index) {
                              <div
                                class="color-segment h-full"
                                [style.width.%]="segment.percentage"
                                [style.background-color]="segment.color"></div>
                            }
                          </div>
                        }
                        <!-- Temperature segments (new - shown when multiple temperatures) -->
                        <!--@if (
                          showTemperatureBackground() &&
                          task.temperatureSegments &&
                          task.temperatureSegments.length > 1
                        ) {
                          <div class="temperature-segment-container mt-0.5 flex h-2">
                            @for (segment of task.temperatureSegments; track $index) {
                              <div
                                class="temperature-segment h-full"
                                [style.width.%]="segment.percentage"
                                [style.background-color]="segment.color"
                                [title]="segment.temperatureUid"></div>
                            }
                          </div>
                        }-->
                      </div>
                    </div>
                    <app-tooltip
                      class="fixed z-100"
                      [target]="timelineTask"
                      [title]="getTooltipTitle(task)"
                      [content]="getTooltipContent(task)"
                      [position]="'top'"
                      [showHint]="edit.canDrag(row, task)"
                      [hintText]="
                        edit.canDrag(row, task) ? editGesturesHint() : getTooltipHint(task)
                      "
                      [interactive]="false" />
                  }
                }

                @if (
                  task.kind === 'pause' ||
                  task.kind === 'home-rest' ||
                  task.kind === 'wait' ||
                  task.kind === 'setup' ||
                  task.kind === 'setdown'
                ) {
                  <div
                    #timelinePauseWaitSetup
                    class="timeline-task group relative"
                    [class]="focusClassForTask(row.uid, task)"
                    [attr.data-context]="
                      task.stepIndex !== undefined ? 'timeline-task' : null
                    "
                    [attr.data-route-uid]="task.routeUid"
                    [attr.data-action-uid]="task.actionUid"
                    [attr.data-step-index]="task.stepIndex"
                    [attr.data-site-uid]="task.siteUid"
                    [attr.data-job-uids]="task.jobUids?.join(' ')"
                    [attr.data-start]="task.startDate.toJSON()"
                    [style.left]="`${task._leftPos}px`"
                    [style.width]="`${task._width}px`"
                    (mouseenter)="onTaskHover(task.actionUid)"
                    (mouseleave)="onTaskHover(null)">
                    <div
                      class="timeline-task-block normal_bg absolute top-4 flex h-8 w-full cursor-pointer items-center justify-center overflow-hidden rounded-md border border-white/20 text-white"
                      [class.pending-hatch]="pendingRouteUids().has(row.uid)"
                      [style.max-width]="`${task._width}px`"
                      [style.--color]="task.lazyProps?.color ?? taskDefaultColor"
                      [style.box-shadow]="
                        isTaskRinged(task)
                          ? '0 0 0 1px var(--accent-main), 0 0 0 2px var(--accent-interactive)'
                          : null
                      "
                      (click)="onTaskClick(task, $event)"
                      (keydown)="onTaskClick(task, $event)">
                      @if (task.kind === 'pause' && (task._width ?? 0) > 21) {
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                          role="img"
                          class="iconify iconify--ion"
                          width="18"
                          height="18"
                          preserveAspectRatio="xMidYMid meet"
                          viewBox="0 0 512 512">
                          <path
                            d="m432,70l-336,0a16,16 0 0 0 -16,16l0,192a96.11,96.11 0 0 0 96,96l112,0a96.11,96.11 0 0 0 96,-96l0,-80l18,0a62.07,62.07 0 0 0 62,-62l0,-34a32,32 0 0 0 -32,-32zm0,66a30,30 0 0 1 -30,30l-18,0l0,-64l48,0l0,34z"
                            fill="currentColor"></path>
                          <path
                            d="m400,410l-336,0a16,16 0 0 0 0,32l336,0a16,16 0 0 0 0,-32z"
                            fill="currentColor"></path>
                        </svg>
                      }

                      @if (task.kind === 'home-rest' && (task._width ?? 0) > 21) {
                        <i
                          aria-hidden="true"
                          data-testid="home-rest-icon"
                          [class]="ICONS.houseFill"
                          class="text-lg leading-none"></i>
                      }

                      @if (task.kind === 'wait' && (task._width ?? 0) > 21) {
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                          role="img"
                          class="iconify iconify--ion"
                          width="18"
                          height="18"
                          preserveAspectRatio="xMidYMid meet"
                          viewBox="0 0 512 512">
                          <path
                            d="m64,24c0,-13.3 10.7,-24 24,-24l336,0c13.3,0 24,10.7 24,24s-10.7,24 -24,24l-8,0l0,19c0,40.3 -16,79 -44.5,107.5l-81.6,81.5l81.5,81.5c28.6,28.5 44.6,67.2 44.6,107.5l0,19l8,0c13.3,0 24,10.7 24,24s-10.7,24 -24,24l-336,0c-13.3,0 -24,-10.7 -24,-24s10.7,-24 24,-24l8,0l0,-19c0,-40.3 16,-79 44.5,-107.5l81.6,-81.5l-81.6,-81.5c-28.5,-28.5 -44.5,-67.2 -44.5,-107.5l0,-19l-8,0c-13.3,0 -24,-10.7 -24,-24zm110.5,347.5c-3.9,3.9 -7.5,8.1 -10.7,12.5l184.4,0c-3.2,-4.4 -6.8,-8.6 -10.7,-12.5l-81.5,-81.6l-81.5,81.5l0,0.1zm173.7,-243.5c12.8,-17.6 19.8,-39 19.8,-61l0,-19l-224,0l0,19c0,22.1 7,43.4 19.8,61l184.4,0z"
                            fill="currentColor"></path>
                        </svg>
                      }

                      @if (
                        (task.kind === 'setup' || task.kind === 'setdown') &&
                        (task._width ?? 0) > 21
                      ) {
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                          role="img"
                          class="iconify iconify--ion"
                          width="18"
                          height="18"
                          preserveAspectRatio="xMidYMid meet"
                          viewBox="0 0 512 512">
                          <path
                            d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"
                            fill="currentColor"></path>
                        </svg>
                      }
                    </div>
                  </div>
                  <app-tooltip
                    class="fixed z-100"
                    [target]="timelinePauseWaitSetup"
                    [title]="getTooltipTitle(task)"
                    [content]="getTooltipContent(task)"
                    [position]="'top'"
                    [showHint]="true"
                    [hintText]="getTooltipHint(task)"
                    [interactive]="true" />
                }
              }

              @if (edit.editingHere() && row.route) {
                @if (geometry(); as geometry) {
                  <app-timeline-edit-tours [row]="row" [geometry]="geometry" />
                }
              }
              @if (editTargets()[row.uid]; as targets) {
                @if (geometry(); as geometry) {
                  <app-timeline-edit-targets
                    [targets]="targets"
                    [hot]="editHotTarget()"
                    [geometry]="geometry"
                    [carried]="
                      editPlacing()?.overRowUid === row.uid ? editPlacing() : null
                    " />
                }
              }
              @if (carryRowUid() === row.uid) {
                @if (editDrag(); as drag) {
                  @if (geometry(); as geometry) {
                    <app-timeline-edit-overlay
                      layer="row"
                      [drag]="drag"
                      [geometry]="geometry" />
                  }
                }
              }
            </div>
          </div>
        </div>
      </cdk-virtual-scroll-viewport>
    </div>

    <!-- Pinned under the rows rather than scrolled with them: it stays in reach mid-drag. -->
    @if (edit.editingHere()) {
      <div
        class="new-route-row border-default flex h-12 shrink-0 border-t"
        data-testid="timeline-new-route-row"
        [class.is-carrying]="newRouteSlot().carried !== null"
        [class.is-hot]="newRouteSlot().hot">
        <div
          class="border-default bg-surface flex shrink-0 items-center border-r px-2"
          [style.width.px]="cellLegendWidth">
          <button
            type="button"
            class="text-accent-main hover:bg-interactive flex w-full cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-1 text-xs font-semibold transition-colors"
            (click)="openNewRoute($event)">
            <i [class]="ICONS.plus" aria-hidden="true"></i>
            {{ 'features.plan.edit.new_route.button' | translate }}
          </button>
        </div>
        <div class="min-w-0 flex-1 px-2 py-1.5" [attr.data-edit-row]="NEW_ROUTE_ROW">
          <div class="new-route-slot">
            @if (newRouteSlot().carried; as name) {
              <i [class]="ICONS.plus" aria-hidden="true"></i>
              <span class="truncate">
                {{
                  (newRouteSlot().hot
                    ? 'features.plan.edit.new_route.hot'
                    : 'features.plan.edit.new_route.carrying'
                  ) | translate: { name }
                }}
              </span>
            } @else {
              <span class="truncate">{{
                'features.plan.edit.new_route.idle' | translate
              }}</span>
            }
          </div>
        </div>
      </div>
    }
  } @else {
    <div class="flex h-full w-full flex-col justify-center">
      <i class="ph-bold ph-plugs mx-auto py-2 text-2xl"></i>
      <p class="text-center text-zinc-700 dark:text-zinc-300">
        {{ 'visualization.timeline.empty' | translate }}
      </p>
    </div>
  }

  <!-- Timeline step popup outlet (positions itself via floating-ui, one instance) -->
  <app-timeline-step-popup-outlet
    [occupationSeries]="occupationSeries()"
    (add)="onAddComment($event)"
    (editComment)="onEditComment($event)"
    (removeComment)="onRemoveComment($event)"
    (toggleResolve)="onToggleResolve()" />
</div>
